// ─── API CLIENT ───────────────────────────────────────────────────────────────
// Axios instance with:
//   - Auth header injection
//   - Automatic token refresh on 401
//   - Structured error handling
//   - Request/response logging in dev
// ─────────────────────────────────────────────────────────────────────────────

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth.store';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Instância SEM os interceptors abaixo, usada só para a própria chamada de
// refresh — reaproveitar `apiClient` ali causaria um deadlock real: se
// POST /auth/refresh (refreshToken também inválido/revogado) responder 401,
// esse 401 passaria de novo pelo response interceptor de `apiClient`; como
// `isRefreshing` já estaria `true` (setado antes desta própria chamada), o
// interceptor colocaria essa resposta na `failedQueue` em vez de rejeitar —
// e nada nunca chama `processQueue` para destravá-la, então o `await
// apiClient.post('/auth/refresh', ...)` original nunca resolve nem lança,
// travando a tela para sempre (bug real, encontrado ao investigar uma
// sessão presa em produção).
const refreshClient = axios.create({ baseURL: API_BASE_URL, timeout: 30_000 });

// ── Request Interceptor — inject auth token ───────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ── Response Interceptor — handle errors and token refresh ───────────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

function redirecionarParaLogin(): void {
  // Evita reatribuir a navegação repetidamente se várias requisições em
  // paralelo (ex.: os vários cards do Dashboard) chegarem aqui ao mesmo
  // tempo — e evita um loop se o próprio /auth/login disparar alguma
  // chamada que também 401e.
  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login';
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Uma requisição que JÁ passou por um ciclo de renovação (originalRequest
    // com _retry=true) e ainda assim recebeu 401 de novo significa que o
    // token renovado também não é válido (ou a sessão foi revogada/expirou
    // entre o refresh e o retry) — isso é uma falha de autenticação
    // definitiva, não passageira. Sem este branch, a requisição só rejeitava
    // silenciosamente (caía direto no "Transform error response" abaixo),
    // deixando qualquer tela que dependa de várias queries presa em loading
    // para sempre, sem nunca mandar o usuário de volta para o login.
    if (error.response?.status === 401 && originalRequest._retry) {
      useAuthStore.getState().clearAuth();
      redirecionarParaLogin();
      return Promise.reject(error);
    }

    // Handle 401 — try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      const { refreshToken, clearAuth, updateAccessToken } = useAuthStore.getState();

      if (!refreshToken) {
        clearAuth();
        redirecionarParaLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token as string}`;
          }
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshClient.post<{
          success: boolean;
          data: { accessToken: string; expiresAt: string };
        }>('/auth/refresh', { refreshToken });

        const { accessToken, expiresAt } = response.data.data;
        updateAccessToken(accessToken, new Date(expiresAt));
        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuth();
        redirecionarParaLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Transform error response into a consistent format
    const apiError = error.response?.data as
      | { success: false; error: { code: string; message: string } }
      | undefined;

    if (apiError?.error) {
      const enhancedError = new Error(apiError.error.message) as Error & {
        code: string;
        statusCode: number;
        isApiError: true;
      };
      enhancedError.code = apiError.error.code;
      enhancedError.statusCode = error.response?.status ?? 500;
      enhancedError.isApiError = true;
      return Promise.reject(enhancedError);
    }

    return Promise.reject(error);
  },
);

/**
 * Typed GET helper.
 */
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<{ success: true; data: T }>(url, { params });
  return response.data.data;
}

/**
 * Typed POST helper.
 */
export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.post<{ success: true; data: T }>(url, body);
  return response.data.data;
}

/**
 * Typed PATCH helper.
 */
export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.patch<{ success: true; data: T }>(url, body);
  return response.data.data;
}

/**
 * Typed DELETE helper.
 */
export async function apiDelete<T = void>(url: string): Promise<T> {
  const response = await apiClient.delete<{ success: true; data: T }>(url);
  return response.data.data;
}

/**
 * Typed PUT helper.
 */
export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.put<{ success: true; data: T }>(url, body);
  return response.data.data;
}
